/**
 * WERSJA „OBECNA” (produkcja 0.31.3) — harmonogram na karcie pojazdu, kopia z `main`
 * (`git show origin/main:apps/app/src/admin/fleet/VehicleSchedule.tsx`), 12.10.2026.
 *
 * Decyzja użytkownika 12.10.2026: „obecna” wersja aplikacji ma zachowywać
 * się dokładnie jak produkcja, a zmiany z develop są za przełącznikami.
 * Z tego pliku korzysta karta pojazdu przy wyłączonym przełączniku
 * `vehicleCardPrototype` (`fleet/CarShow.tsx`). Zmieniamy go tylko po to,
 * żeby wpiąć inne przełączniki (sekcja domyślnie niewidoczna) — przy
 * wszystkich wyłączonych ekran ma być taki jak na produkcji. Po decyzji
 * „wchodzi” albo „odrzucone” katalog `legacy/` znika razem z przełącznikiem.
 */
import { useEffect, useState } from 'react';
import { Link, useRecordContext, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  type ChipProps,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { SectionCard, fmtDay } from '../../../shared/Panel';
import { clientDayKey, shortDateTime } from '../../../shared/dateTime';
import { TABLE_SCROLL_SX } from '../../../shared/tableScroll';
import { toErrorMessage } from '../../userOffers/orderActionsApi';
import { recordScoped } from '../../../shared/recordScoped';
import { plural } from '../../../shared/plural';
import { fetchVehiclePlan, type PlanItem, type VehiclePlan } from '../vehiclePlanApi';
import { PlanLegend, PlanTimeline } from '../PlanTimeline';
import { routeText, shiftDayKey } from '../timeline';
import {
  PLAN_PHASES,
  PLANNED_SHOWN,
  arrangePlanRows,
  planPhase,
  planRowStatus,
  rangeText,
  tmsStatusText,
  type PlanRowState,
} from '../planRows';
import { scheduleStatusLabel } from '../carScheduleApi';
import { hasSegments } from '../planSegments';

/**
 * Harmonogram pojazdu — obsadzenia i warianty na osi dni, tylko odczyt.
 *
 * ## Po co ta sekcja
 *
 * Status z 29.09.2026: „finalne przypisanie pojazdu do zlecenia musi być
 * odzwierciedlone w planie pracy floty, widoczne np. w harmonogramie danego
 * pojazdu” — planiści pracują z listy aut. Do tego dnia sekcja pokazywała
 * wyłącznie okna miesięczne (`CarSchedule`), których na produkcji nie ma, więc
 * dla każdego auta mówiła „nie ma jeszcze okien harmonogramu”.
 *
 * Plan bierze się **ze zleceń**: obsadzenia (pewne) i warianty (wstępne).
 * Okno miesięczne zostaje kontekstem — serwis widać jako zakreskowanie.
 * Pasek to szacowana zajętość, nie całe okno zlecenia (opis pod osią).
 *
 * Oś w trzech liniach (omówienie 01.10.2026): zrealizowane, w realizacji,
 * zaplanowane — z oknem zlecenia linią przerywaną i szacunkiem wykonania
 * pełnym paskiem (`PlanTimeline.tsx`). Do 06.10.2026 linie były dwie:
 * obsadzenia i warianty.
 *
 * Wiersz „Kierowca (wg harmonogramu)” — kto jeździ autem w danym tygodniu wg
 * harmonogramu kierowców; tydzień przepisany z wcześniejszego miesiąca
 * (tymczasowo, do czasu regularnego źródła pliku) jest szary, kursywą. To
 * kontekst pod trzema liniami zleceń, nie czwarta linia zleceń.
 *
 * Tabela pod osią mieści się w karcie od 1366 px (06.10.2026, test e2e:
 * przy 9 kolumnach z godzinami w jednej linii przewijała się w bok o 249 px,
 * „Obsadził” poza ekranem). Siedem kolumn: terminy od klienta to jedna komórka
 * w dwóch liniach (załadunek, rozładunek), „Źródło” niesie w drugiej linii,
 * kto obsadził; teksty się zawijają, a godzina nie odrywa się od swojej
 * daty (spacja nierozdzielająca).
 */

/** Długość widoku przy przewijaniu — ta sama co domyślna z API (7 + 21 + dziś). */
const VIEW_DAYS = 29;
const STEP_DAYS = 7;

/** Etykieta czipu w tabeli zawija się zamiast rozpychać kolumnę. */
const WRAPPING_CHIP_SX = {
  height: 'auto',
  maxWidth: 140,
  '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25, lineHeight: 1.25 },
} as const;

const KindChip = ({ item }: { item: PlanItem }) =>
  item.kind === 'assignment' ? (
    <Chip size="small" color="primary" label="obsadzenie" />
  ) : (
    <Chip
      size="small"
      variant="outlined"
      color={item.assignedElsewhere ? 'error' : 'warning'}
      label={item.assignedElsewhere ? 'wariant (zlecenie już obsadzone)' : 'wariant'}
      title={item.assignedElsewhere ? 'Zlecenie jest już obsadzone innym pojazdem.' : undefined}
      sx={{ ...WRAPPING_CHIP_SX, borderStyle: 'dashed' }}
    />
  );

/**
 * Kolory jak na osi (06.10.2026): w realizacji niebieskie, zaplanowane
 * szare, zrealizowane zielone, wariant przerywany.
 */
const STATE_LOOK: Record<
  PlanRowState,
  { color: ChipProps['color']; variant: ChipProps['variant']; dashed?: boolean }
> = {
  inProgress: { color: 'primary', variant: 'filled' },
  planned: { color: 'default', variant: 'filled' },
  tentative: { color: 'warning', variant: 'outlined', dashed: true },
  done: { color: 'success', variant: 'outlined' },
  unknown: { color: 'default', variant: 'outlined' },
};

/** Stan wiersza (`planRows.ts`); skąd się wziął — w dymku, ze statusem w TMS. */
const StateChip = ({ item }: { item: PlanItem }) => {
  const { state, label } = planRowStatus(item);
  const look = STATE_LOOK[state];
  return (
    <Chip
      size="small"
      color={look.color}
      variant={look.variant}
      label={label}
      title={tmsStatusText(item) ?? 'Brak statusu w TMS'}
      sx={{ ...WRAPPING_CHIP_SX, ...(look.dashed ? { borderStyle: 'dashed' } : {}) }}
    />
  );
};

/**
 * „Źródło” — skąd wiersz (omówienie 01.10.2026: „to nie jest stan, tylko …
 * proweniencja”). Reguła po stronie API (`planItemSource`); zlecenie spoza
 * TMS ma dopisek. Starsze API bez pola — kreska.
 */
const SourceCell = ({ item }: { item: PlanItem }) => {
  const s = item.source;
  if (!s) return <Box component="span" data-testid="plan-source">—</Box>;
  return (
    <Box component="span" title={s.detail} data-testid="plan-source" sx={{ display: 'block' }}>
      {s.label}
      {s.orderOrigin ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {s.orderOrigin}
        </Typography>
      ) : null}
    </Box>
  );
};

/**
 * Kto obsadził — druga linia komórki „Źródło” (06.10.2026; wcześniej osobna
 * kolumna „Obsadził”). Obsadzenie z importu zrobiono w TMS (poprawa
 * brzmienia 01.10.2026: „w TMS”, wcześniej „import TMS”). Wariant i brak
 * wpisu w dzienniku — nic (źródło mówi już „nieznane”).
 */
const AssignedByLine = ({ item }: { item: PlanItem }) => {
  const a = item.assignedBy;
  if (!a) return null;
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid="plan-assigned-by">
      obsadził: <span>{`${a.username ?? (a.source === 'IMPORT' ? 'w TMS' : '—')}, ${shortDateTime(a.at)}`}</span>
    </Typography>
  );
};

/** Linia komórki z podpisem: „zał. 30.09, 08:00 – 30.09, 12:00”. */
const LabeledLine = ({ label, value }: { label: string; value: string }) => (
  <Box component="span" sx={{ display: 'block' }}>
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {label}
    </Box>{' '}
    <span>{value}</span>
  </Box>
);

/** Komórki tabeli: węższy margines niż domyślny, tekst zawija się u góry. */
const CELL_SX = { px: 1, verticalAlign: 'top' } as const;
const HEAD_SX = { ...CELL_SX, color: 'text.secondary' } as const;

/**
 * Lista pod osią — od tego, co przed autem (omówienie 01.10.2026): w realizacji,
 * dwa najbliższe zaplanowane, potem ostatnie zrealizowane obsadzenie;
 * dalsze zaplanowane i wcześniejsze zrealizowane pod przyciskami.
 * Reguły: `planRows.ts`.
 */
const PlanList = ({ items }: { items: PlanItem[] }) => {
  const [showEarlierDone, setShowEarlierDone] = useState(false);
  const [showAllPlanned, setShowAllPlanned] = useState(false);
  const { rows, hiddenDone, hiddenPlanned } = arrangePlanRows(items, showEarlierDone, showAllPlanned);
  return (
    <>
      {rows.length > 0 ? (
        <Box sx={TABLE_SCROLL_SX}>
          <Table size="small" data-testid="plan-list">
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_SX}>Zlecenie</TableCell>
                <TableCell sx={HEAD_SX}>Rodzaj</TableCell>
                <TableCell sx={HEAD_SX}>Relacja</TableCell>
                <TableCell sx={HEAD_SX}>Terminy od klienta</TableCell>
                <TableCell sx={HEAD_SX}>Szacunek wykonania</TableCell>
                <TableCell sx={HEAD_SX}>Stan</TableCell>
                <TableCell sx={HEAD_SX}>Źródło</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((i) => (
                <TableRow key={`${i.kind}-${i.orderId}`} data-testid="plan-list-row">
                  {/* Numer się nie łamie — to jedyna kolumna bez zawijania. */}
                  <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>
                    <Link to={`/user-offers/${i.orderId}/show`}>{i.number ?? `#${i.orderId}`}</Link>
                  </TableCell>
                  <TableCell sx={CELL_SX}>
                    <KindChip item={i} />
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, minWidth: 110 }}>{routeText(i)}</TableCell>
                  {/* Okno punktu z TMS, a bez niego — granica okna zlecenia. */}
                  <TableCell sx={{ ...CELL_SX, minWidth: 130 }}>
                    <LabeledLine label="zał." value={rangeText(i.loadingFrom ?? i.windowFrom, i.loadingTo)} />
                    <LabeledLine label="rozł." value={rangeText(i.unloadingFrom, i.unloadingTo ?? i.windowTo)} />
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, minWidth: 110 }}>
                    <span>{rangeText(i.occupancyFrom, i.occupancyTo)}</span>
                    {i.occupancyBasis === 'window' ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        całe okno zlecenia (trasa nieznana)
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={CELL_SX}>
                    <StateChip item={i} />
                  </TableCell>
                  <TableCell sx={{ ...CELL_SX, minWidth: 110 }}>
                    <SourceCell item={i} />
                    <AssignedByLine item={i} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      {hiddenPlanned > 0 ? (
        <Button size="small" onClick={() => setShowAllPlanned((v) => !v)} sx={{ mt: 0.5 }}>
          {showAllPlanned
            ? `Pokaż tylko ${PLANNED_SHOWN} najbliższe zaplanowane`
            : `Pokaż dalsze zaplanowane (${hiddenPlanned})`}
        </Button>
      ) : null}
      {hiddenDone > 0 ? (
        <Button size="small" onClick={() => setShowEarlierDone((v) => !v)} sx={{ mt: 0.5 }}>
          {showEarlierDone
            ? 'Ukryj wcześniejsze zrealizowane'
            : `Pokaż wcześniejsze zrealizowane (${hiddenDone})`}
        </Button>
      ) : null}
    </>
  );
};

const VehicleSchedulePanel = () => {
  const record = useRecordContext<RaRecord>();
  const [fromKey, setFromKey] = useState<string | null>(null);
  const [plan, setPlan] = useState<VehiclePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const id = record?.id;

  useEffect(() => {
    if (id === undefined) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchVehiclePlan(
      id,
      fromKey ? { from: fromKey, to: shiftDayKey(fromKey, VIEW_DAYS - 1) } : {},
    )
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(toErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, fromKey]);

  if (!record) return null;

  const currentFrom = fromKey ?? (plan ? clientDayKey(new Date(plan.from)) : null);
  const shift = (delta: number) => {
    if (currentFrom) setFromKey(shiftDayKey(currentFrom, delta));
  };
  const view = plan ? { from: new Date(plan.from), to: new Date(plan.to) } : null;
  const assignments = plan?.items.filter((i) => i.kind === 'assignment') ?? [];
  const candidates = plan?.items.filter((i) => i.kind === 'candidate') ?? [];

  return (
    <SectionCard
      title={`Harmonogram (${plural(assignments.length, 'obsadzenie', 'obsadzenia', 'obsadzeń')}, ${plural(candidates.length, 'wariant', 'warianty', 'wariantów')})`}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <ButtonGroup size="small" variant="outlined" disabled={!currentFrom || loading}>
          <Button onClick={() => shift(-STEP_DAYS)}>‹ tydzień</Button>
          <Button onClick={() => setFromKey(null)}>dziś</Button>
          <Button onClick={() => shift(STEP_DAYS)}>tydzień ›</Button>
        </ButtonGroup>
        {plan ? (
          <Typography variant="body2" color="text.secondary">
            {fmtDay(plan.from)} – {fmtDay(plan.to)}
          </Typography>
        ) : null}
        {loading ? <CircularProgress size={18} /> : null}
      </Box>
      {loadError ? <Alert severity="error">{loadError}</Alert> : null}
      {plan && view ? (
        <>
          {plan.months.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {/* Bez żargonu „Okno …” i bez angielskich wartości z bazy
                  (poprawa brzmienia 01.10.2026) — sam okres i status. */}
              {plan.months.map((m) => (
                <Chip
                  key={m.id}
                  size="small"
                  variant="outlined"
                  color={m.unavailable ? 'warning' : 'success'}
                  label={`${fmtDay(m.from)} – ${fmtDay(m.to)}: ${scheduleStatusLabel(m.status)}`}
                  title="Status pojazdu w harmonogramie miesięcznym"
                />
              ))}
            </Box>
          ) : null}
          <PlanTimeline
            window={view}
            labelWidth={110}
            dayMinWidth={40}
            size="large"
            rows={[
              // Trzy linie (omówienie 01.10.2026): zrealizowane, w realizacji,
              // zaplanowane — warianty w „zaplanowanych”, z przerywanym obramowaniem.
              ...PLAN_PHASES.map(({ phase, label }) => ({
                key: phase,
                label: <Typography variant="caption">{label}</Typography>,
                items: plan.items.filter((i) => planPhase(i) === phase),
                months: plan.months,
              })),
              {
                key: 'driver',
                label: (
                  <Typography variant="caption" sx={{ lineHeight: 1.1, display: 'block' }}>
                    Kierowca (wg harmonogramu)
                  </Typography>
                ),
                items: [],
                driverWeeks: plan.driverWeeks ?? [],
              },
            ]}
          />
          {/* Odcinek „wg planu TMS” — tylko z przełącznikiem `occupiedUntilTmsPlan`
              (12.10.2026); bez niego API pól planu nie oddaje, oś ich nie rysuje. */}
          <PlanLegend
            driverWeeks
            segments={plan.items.some(hasSegments)}
            tmsPlan={plan.items.some((i) => i.tmsPlanSegment)}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {plan.occupancyNote}
            {plan.segmentsNote ? ` ${plan.segmentsNote}` : ''}
            {plan.tmsPlanNote ? ` ${plan.tmsPlanNote}` : ''} Godziny w czasie polskim.
          </Typography>
          {plan.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Brak obsadzeń i wariantów w okresie {fmtDay(plan.from)} – {fmtDay(plan.to)}.
            </Typography>
          ) : (
            <Box sx={{ mt: 1 }}>
              <PlanList items={plan.items} />
            </Box>
          )}
        </>
      ) : null}
    </SectionCard>
  );
};

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const VehicleSchedule = recordScoped(VehicleSchedulePanel);
